import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./store";

/**
 * App-level filter state: which serials the user selected for the upcoming
 * "data from specific serials" filtering. The slice stores the CHECKED
 * mpKeys, kept in sync with the serial list by `syncSerialSelection`:
 * - before the user touches anything (`touched === false`), a loaded list
 *   selects everything (the "all checked" default);
 * - after the user toggles, their choice is preserved and only pruned of
 *   serials that no longer exist.
 */
export interface SerialFilterState {
  /** Checked serial `MPKey`s. */
  selected: string[];
  /** False until the user's first toggle — gates the select-all default. */
  touched: boolean;
}

const initialState: SerialFilterState = { selected: [], touched: false };

export const serialFilterSlice = createSlice({
  name: "serialFilter",
  initialState,
  reducers: {
    /** Toggles one serial; checking adds its mpKey, unchecking removes it. */
    setSerialChecked(
      state,
      action: PayloadAction<{ mpKey: string; checked: boolean }>,
    ) {
      const { mpKey, checked } = action.payload;
      state.touched = true;
      if (checked) {
        if (!state.selected.includes(mpKey)) {
          state.selected.push(mpKey);
        }
      } else {
        state.selected = state.selected.filter((key) => key !== mpKey);
      }
    },
    /** Check-all shortcut — back to the "everything included" default. */
    setAllSerialsChecked(state, action: PayloadAction<string[]>) {
      state.selected = action.payload;
      state.touched = false;
    },
    /**
     * Group-level toggle (a whole machine line, HD/HDX/NX, …): checks or
     * unchecks every serial of the group in one dispatch. A no-op call
     * (the group already sits fully in the requested state) keeps
     * `touched` untouched, so the all-checked default still auto-syncs.
     */
    setGroupSerialsChecked(
      state,
      action: PayloadAction<{ mpKeys: string[]; checked: boolean }>,
    ) {
      const { mpKeys, checked } = action.payload;
      if (checked) {
        const missing = mpKeys.filter((key) => !state.selected.includes(key));
        if (missing.length === 0) {
          return;
        }
        state.touched = true;
        state.selected.push(...missing);
      } else {
        const keys = new Set(mpKeys);
        const remaining = state.selected.filter((key) => !keys.has(key));
        if (remaining.length === state.selected.length) {
          return;
        }
        state.touched = true;
        state.selected = remaining;
      }
    },
    /**
     * Syncs the selection with a freshly loaded serial list: an untouched
     * selection follows the list (all checked), a user-touched selection is
     * only pruned of serials that disappeared.
     */
    syncSerialSelection(state, action: PayloadAction<string[]>) {
      const mpKeys = action.payload;
      state.selected = state.touched
        ? state.selected.filter((key) => mpKeys.includes(key))
        : [...mpKeys];
    },
  },
});

export const {
  setSerialChecked,
  setAllSerialsChecked,
  setGroupSerialsChecked,
  syncSerialSelection,
} = serialFilterSlice.actions;

/** A serial is included when its mpKey IS in the selected list. */
export function selectIsSerialChecked(state: RootState, mpKey: string): boolean {
  return state.serialFilter.selected.includes(mpKey);
}

export const serialFilterReducer = serialFilterSlice.reducer;
